import React from 'react'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { BackButton } from '../components/BackButton'
import ContactCard from '../components/ContactCard'
import { PageContainer } from '../components/layout/PageContainer'
import YesNoAction from '../components/YesNoAction'
import {
  selectAllFarmerAccounts,
  selectContactInfo,
  selectCurrentFarmerAccount,
} from '../features/azure-b2c/storage'
import CartSlice, { selectOrderConfirmationById } from '../features/cart/storage/cart.slice'
import { useAppDispatch } from '../store/rootStore'
import { TestIds } from '../testIds'
import { CartDeliveryMode } from '../features/cart/storage/cart.types'

export const CheckoutSuccess = () => {
  const params = useParams()

  const farmlandsAccounts = useSelector(selectAllFarmerAccounts)
  const currentAccount = useSelector(selectCurrentFarmerAccount)
  const confirmationId = params.confirmationId
  const confirmedOrder = useSelector(selectOrderConfirmationById(confirmationId))
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const deliveryMode: CartDeliveryMode = location.state?.deliveryMode
  const relationshipOwner = useSelector(selectContactInfo)

  React.useEffect(() => {
    if (!currentAccount || !confirmedOrder || confirmedOrder.status !== 'fulfilled') {
      navigate('/products')
    } else if (currentAccount) {
      dispatch(
        CartSlice.actions.clearConfirmationId({ accountNumber: currentAccount.accountNumber }),
      )
    }
  }, [confirmedOrder, currentAccount])

  const cartOrMessage = React.useMemo(() => {
    const bodyForPickup = (
      <>
        <Typography variant='body2' sx={{ mb: 2 }}>
          Your order has been sent to Farmlands {confirmedOrder?.store?.orgUnitName} for processing
          and the team will be in contact with you directly to let you know when it is ready to pick
          up.
        </Typography>
        <Typography variant='body2' sx={{ mb: 2 }}>
          You will receive an email confirmation of your order and your purchase will also be
          reflected on the Past Products page. It may take some time for this to occur.
        </Typography>
      </>
    )

    const bodyForDelivery = (
      <Typography variant='body2' sx={{ mb: 2 }}>
        You will receive an email confirmation that your order has been received. Before your order
        is processed, <b>we will reach out to confirm any additional delivery costs</b>, if
        applicable.
      </Typography>
    )
    if (confirmedOrder && confirmedOrder.status === 'fulfilled') {
      return (
        <>
          <YesNoAction
            display='Order submitted'
            subheading={
              <>
                <Typography variant='h4' sx={{ mt: 4, mb: 2 }}>
                  Thanks {farmlandsAccounts?.contactFirstName}!
                </Typography>
                <Typography
                  variant='body2'
                  fontWeight='bold'
                  sx={{ mt: 2, mb: 2 }}
                  data-testid={TestIds.feat.checkout.confirmationId}
                >
                  Confirmation ID: {confirmedOrder?.confirmationId}
                </Typography>
              </>
            }
            body={deliveryMode === CartDeliveryMode.PICKUP ? bodyForPickup : bodyForDelivery}
          />

          {deliveryMode === CartDeliveryMode.DELIVERY ? (
            <ContactCard
              sx={{ mt: 4 }}
              title='If you have any questions about your order please contact a Farmlands representative.'
              {...relationshipOwner}
              data-testid={TestIds.components.contactCard.card}
            />
          ) : (
            <ContactCard
              sx={{ mt: 4 }}
              title='If you have any questions please contact your chosen Farmlands store.'
              body={
                <Typography variant='body1' color='white' fontWeight={700} lineHeight={'27px'}>
                  <span>{confirmedOrder?.store?.orgUnitName}</span>
                  <br />
                  <Typography variant='body1' color='white' component='span'>
                    {confirmedOrder?.store?.address}
                  </Typography>
                </Typography>
              }
              email={confirmedOrder?.store?.email}
              phone={confirmedOrder?.store?.phone}
              titleGap={24}
              storeName={confirmedOrder?.store?.orgUnitName}
              data-testid={TestIds.components.contactCard.card}
            />
          )}
        </>
      )
    }
  }, [confirmedOrder, currentAccount])

  return (
    <PageContainer data-testid={TestIds.pages.cart}>
      <BackButton text='Products' onClick={() => navigate('/products')} />
      {cartOrMessage}
    </PageContainer>
  )
}
